import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, X } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { CATEGORY_ICONS, CategoryIcon } from "@/lib/categoryIcons";

const isImage = (v) => /^(https:\/\/|\/api\/files\/)/.test(v || "");

// Category icon picker: ready-made icon grid, or your own image (upload or https link).
export const IconPicker = ({ value, onChange }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const [link, setLink] = useState(isImage(value) ? value : "");
  useEffect(() => { if (isImage(value)) setLink(value); else if (value) setLink(""); }, [value]);
  const typeLink = (t) => {
    setLink(t);
    if (/^https:\/\/\S{4,}$/.test(t.trim())) onChange(t.trim());  // only a full https link replaces the icon
  };
  const upload = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 2_000_000) return toast.error("Image must be under 2 MB");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      onChange((await api.post("/admin/branding/upload", fd)).data.url);
      toast.success("Icon uploaded - remember to save");
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-2" data-testid="icon-picker">
      <div className="flex items-center gap-2">
        <CategoryIcon icon={value} name="?" className="h-10 w-10" size={20} />
        <span className="text-xs text-slate-500" data-testid="icon-picker-current">{value ? (CATEGORY_ICONS[value]?.[1] || "Your image") : "No icon (first letter is shown)"}</span>
        {value && <button type="button" onClick={() => onChange("")} className="ml-auto text-xs text-slate-500 hover:text-red-600 flex items-center gap-1" data-testid="icon-picker-clear"><X size={13} /> Clear</button>}
      </div>
      <div className="grid grid-cols-8 sm:grid-cols-11 gap-1 max-h-40 overflow-y-auto p-1 border border-slate-200 rounded-md">
        {Object.entries(CATEGORY_ICONS).map(([k, [Icon, label]]) => (
          <button type="button" key={k} title={label} aria-label={label} onClick={() => onChange(k)} data-testid={`icon-option-${k}`}
            className={`h-8 w-8 grid place-items-center rounded-md transition-colors ${value === k ? "bg-emerald-600 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
            <Icon size={16} />
          </button>
        ))}
      </div>
      <div className="flex gap-2">
        <input className="flex-1 h-9 rounded-md border border-slate-200 px-3 text-sm" placeholder="Or paste an https:// image link"
          value={link} onChange={(e) => typeLink(e.target.value)} data-testid="icon-picker-url" />
        <button type="button" disabled={busy} onClick={() => ref.current?.click()} data-testid="icon-picker-upload"
          className="h-9 px-3 rounded-md border border-slate-200 text-sm font-semibold flex items-center gap-1.5 hover:bg-slate-50 disabled:opacity-50">
          <Upload size={14} /> {busy ? "Uploading…" : "Upload"}
        </button>
        <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml,image/gif" className="hidden" onChange={upload} data-testid="icon-picker-file" />
      </div>
    </div>
  );
};
